import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Target, Eye, Lightbulb, Award } from 'lucide-react';
import { AnimatedTestimonials } from '@/components/ui/animated-testimonials';


const About = () => {
  const heroRef = useRef(null);
  const teamRef = useRef(null);
  const storyRef = useRef(null);
  const valuesRef = useRef(null);
  const containerRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const teamInView = useInView(teamRef, { once: true, margin: "-100px" });
  const storyInView = useInView(storyRef, { once: true, margin: "-100px" });
  const valuesInView = useInView(valuesRef, { once: true, margin: "-100px" });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0 }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0 }
  };

  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const values = [
    {
      icon: Target,
      title: "Precision",
      description: "Every detail matters. We craft solutions with meticulous attention to quality and performance."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "Pushing boundaries with cutting-edge technology and creative problem-solving approaches."
    },
    {
      icon: Eye,
      title: "Vision",
      description: "We see beyond the present, building scalable solutions that grow with your business."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "Delivering nothing but the best. Premium quality is our standard, not an exception."
    }
  ];

  const team = [
    {
      name: "Reagan .C. Mbao",
      role: "CEO & Founder",
      image: "https://customer-assets.emergentagent.com/job_reagans-design/artifacts/7e89e0pr_Emergent%20profile.jpg",
      bio: "A visionary technologist with a passion for creating digital experiences that transcend expectations. Reagan founded REAGANS with the belief that every business deserves access to world-class digital solutions."
    },
    {
      name: "Chabota Siyingwa",
      role: "Co-Founder & Partner",
      image: "https://customer-assets.emergentagent.com/job_reagans-design/artifacts/069d5pt8_IMG-20260115-WA0023.jpg",
      bio: "A strategic innovator bringing expertise in business development and client relations. Chabota ensures every project aligns with client goals while maintaining the highest standards of quality and professionalism."
    }
  ];

  const testimonialsData = [
    {
      quote: "REAGANS didn't just build us a website; they architected a digital ecosystem that has scaled effortlessly with our rapid growth. Their expertise in AI integration is world-class.",
      name: "Marcus Thorne",
      designation: "CEO at NexGen Solutions",
      src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=3000&auto=format&fit=crop",
    },
    {
      quote: "The level of craftsmanship in their design is unparalleled. Our conversion rates increased by 40% within the first month of launching our new premium platform.",
      name: "Sophia Valerius",
      designation: "Marketing Director at Aura Luxury",
      src: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=3000&auto=format&fit=crop",
    },
    {
      quote: "Their team's ability to translate complex business requirements into intuitive user experiences is a game-changer. They are truly partners in our innovation journey.",
      name: "Julian Chen",
      designation: "Founder of Veloce Tech",
      src: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=3000&auto=format&fit=crop",
    },
    {
      quote: "Strategic, efficient, and technologically superior. They delivered a custom infrastructure that solved operational bottlenecks we'd struggled with for years.",
      name: "Elena Rodriguez",
      designation: "COO at Global Logistics Systems",
      src: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=3000&auto=format&fit=crop",
    },
    {
      quote: "Working with REAGANS has been the best investment for our digital presence. Their attention to detail and commitment to excellence is visible in every pixel.",
      name: "David Sterling",
      designation: "Managing Partner at Sterling & Co.",
      src: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=3000&auto=format&fit=crop",
    },
  ];

  return (
    <div data-testid="about-page" className="relative" ref={containerRef}>
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-[50vh] flex items-center pt-32 pb-16 overflow-hidden"
      >
        {/* Layered Background */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute inset-0"
            style={{ y: bgY }}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=80')`,
                filter: 'brightness(0.3) saturate(0.8)'
              }}
            />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
          <div className="absolute inset-0 bg-mesh opacity-60" />
        </div>

        <motion.div
          className="absolute top-1/3 right-0 w-96 h-96 bg-gold/8 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.08, 0.15, 0.08] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <motion.div
          variants={stagger}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12"
        >
          <motion.span
            variants={fadeInUp}
            transition={{ duration: 0.8 }}
            className="font-mono text-xs uppercase tracking-[0.2em] text-gold/60 mb-4 block"
          >
            About Us
          </motion.span>

          <motion.h1
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-none mb-6"
          >
            <span className="text-white">Crafting Digital</span>
            <br />
            <span className="text-gradient-gold">Masterpieces</span>
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-white/60 text-base md:text-lg max-w-2xl leading-relaxed"
          >
            REAGANS is a premium digital solutions provider, specializing in
            creating exceptional web experiences, AI-powered systems, and
            custom digital infrastructure for businesses worldwide.
          </motion.p>
        </motion.div>
      </section>

      {/* Team Section - Moved to Top */}
      <section
        ref={teamRef}
        className="relative py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={teamInView ? "visible" : "hidden"}
          >
            <motion.div
              variants={fadeInUp}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold/60 mb-4 block">
                Our Leadership
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-medium text-white">
                Meet The <span className="text-gradient-gold">Team</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {team.map((member, index) => (
                <motion.div
                  key={index}
                  variants={index === 0 ? fadeInLeft : fadeInRight}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className="glass-card rounded-3xl p-8 text-center group"
                  whileHover={{ y: -5 }}
                >
                  {/* Profile Image */}
                  <motion.div
                    className="w-36 h-36 md:w-44 md:h-44 mx-auto mb-6 rounded-full bg-gradient-to-br from-gold/30 to-violet-glow/30 p-1"
                    whileHover={{ scale: 1.05 }}
                    animate={{
                      boxShadow: ['0 0 30px rgba(212,175,55,0.2)', '0 0 50px rgba(212,175,55,0.3)', '0 0 30px rgba(212,175,55,0.2)']
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <div
                      className="w-full h-full rounded-full overflow-hidden"
                      style={{
                        backgroundImage: `url('${member.image}')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center top'
                      }}
                      data-testid={`team-member-image-${index}`}
                    />
                  </motion.div>

                  {/* Info */}
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold/60 mb-2 block">
                    {member.role}
                  </span>
                  <h3 className="font-heading text-xl md:text-2xl font-medium text-white mb-4 group-hover:text-gold transition-colors">
                    {member.name}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {member.bio}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section
        ref={storyRef}
        className="relative py-20 md:py-28 bg-background-paper"
      >
        <div className="absolute inset-0 bg-mesh opacity-30" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={storyInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center"
          >
            <motion.div
              variants={fadeInLeft}
              transition={{ duration: 0.8 }}
              className="md:col-span-5"
            >
              <motion.div
                className="glass-card rounded-3xl p-2 glow-gold overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                {/* Laptop with code - back-left angle shot */}
                <div
                  className="w-full aspect-square rounded-2xl bg-cover bg-center"
                  style={{
                    backgroundImage: `url('https://images.unsplash.com/photo-1537498425277-c283d32ef9db?auto=format&fit=crop&w=800&q=80')`,
                    filter: 'brightness(0.85)'
                  }}
                  data-testid="about-story-image"
                />
              </motion.div>
            </motion.div>

            <motion.div
              variants={fadeInRight}
              transition={{ duration: 0.8 }}
              className="md:col-span-7 md:pl-8"
            >
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold/60 mb-4 block">
                Our Story
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-white mb-6">
                Where Vision Meets <span className="text-gradient-gold">Execution</span>
              </h2>
              <div className="space-y-4 text-white/60 leading-relaxed">
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={storyInView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.3 }}
                >
                  REAGANS was founded with a singular vision: to bridge the gap between
                  ambitious business goals and powerful digital solutions. In a world
                  where online presence defines success, we recognized the need for a
                  partner who truly understands both technology and business.
                </motion.p>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={storyInView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.4 }}
                >
                  Today, we serve clients across the globe, from startups seeking their
                  first digital footprint to established businesses looking to revolutionize
                  their operations with AI-powered systems and custom digital infrastructure.
                </motion.p>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={storyInView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.5 }}
                >
                  Every project we undertake is approached with the same philosophy:
                  uncompromising quality, innovative thinking, and a relentless focus
                  on delivering measurable results for our clients.
                </motion.p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section
        ref={valuesRef}
        className="relative py-20 md:py-28 overflow-hidden"
      >
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=2000&q=80')`
            }}
          />
          <div className="absolute inset-0 bg-mesh opacity-50" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={valuesInView ? "visible" : "hidden"}
          >
            <motion.div
              variants={fadeInUp}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold/60 mb-4 block">
                Our Values
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-medium text-white">
                What Drives <span className="text-gradient-gold">Us</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  data-testid={`value-card-${index}`}
                  className="glass-card p-8 rounded-2xl group text-center cursor-pointer"
                  whileHover={{ y: -10, transition: { duration: 0.3 } }}
                >
                  <motion.div
                    className="w-14 h-14 rounded-2xl bg-gold/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-gold/20 transition-colors"
                    whileHover={{ rotate: 10, scale: 1.1 }}
                  >
                    <value.icon size={28} strokeWidth={1.5} className="text-gold" />
                  </motion.div>
                  <h3 className="font-heading text-xl font-medium text-white mb-3 group-hover:text-gold transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold/60 mb-4 block">
              Testimonials
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium text-white">
              What Our <span className="text-gradient-gold">Clients Say</span>
            </h2>
          </motion.div>
        </div>
        <AnimatedTestimonials testimonials={testimonialsData} autoplay={true} />
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-28 bg-background-paper">
        <div className="absolute inset-0 bg-mesh opacity-30" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative max-w-7xl mx-auto px-6 md:px-12 text-center"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-white mb-6">
            Ready to Work <span className="text-gradient-gold">Together?</span>
          </h2>
          <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto mb-10">
            Let's discuss how we can bring your vision to life with our expertise and dedication.
          </p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-3 glass-button px-10 py-5 rounded-full font-mono text-sm uppercase tracking-widest text-gold group animate-pulse-glow"
            >
              Get in Touch
              <ArrowRight size={18} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default About;
